import { Directive } from '@angular/core';
import { Query } from '@datorama/akita';
import { ChartConfiguration, ChartType } from 'chart.js';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { TrialInsightsState } from '@models/trial-insights/trial-insights-store.model';
import { TrialInsightsStore } from '@models/trial-insights/trial-insights.store';

import { SortOrder } from '@shared/services/gql.service';

import { TrialInsightsChartModel } from '../models/trial-insights-chart.model';
import { TrialInsightsLegendOptions } from '../models/trial-insights-legend.model';
import {
  TrialInsightsTableModel,
  TrialInsightsTableOptions,
} from '../models/trial-insights-table.model';

export interface BaseConfig {
  slice: keyof StateActual;
  sortDefaults: SortOrderDefault[];
}
export interface ConstructorConfig<CType extends ChartType> extends BaseConfig {
  chartService: TrialInsightsChartModel<CType>;
  tableService: TrialInsightsTableModel;
  trialInsightsQuery: StoreQuery;
}

export interface SortOrderDefault {
  buttonKey: string;
  defaultOrder: SortOrder;
}
export type StateActual = Omit<TrialInsightsState, 'currentOpenMonth'>;

export type StateSlice = StateActual[keyof StateActual];

export type StoreActual = TrialInsightsStore;

export type StoreData<T> = {
  initialRequest: StoreDetail<T>;
  remainingRequest: StoreDetail<T>;
};

export type StoreDetail<T> = null | T;

export type StoreQuery = Query<TrialInsightsState>;

@Directive()
export class GenericTrialInsightsQuery<CType extends ChartType = ChartType> {
  chartFn: () => ChartConfiguration<CType>;
  chartOptions: BehaviorSubject<ChartConfiguration<CType>>;
  chartService: TrialInsightsChartModel<CType>;
  data: Observable<null | StateSlice['data'] | undefined>;
  expectedEnrolled: BehaviorSubject<string>;
  expectedEnrolledExceeded: BehaviorSubject<boolean>;
  isLoading: Observable<StateSlice['isLoading'] | undefined>;
  isLoadingRemaining: Observable<StateSlice['isLoadingRemaining'] | undefined>;
  legendFn: () => TrialInsightsLegendOptions;
  legendOptions: BehaviorSubject<TrialInsightsLegendOptions>;
  selectedKey: BehaviorSubject<string>;
  sortDefaults: SortOrderDefault[];
  sortOrder: BehaviorSubject<SortOrder>;
  sortSeen: string[];
  store: Observable<StateSlice>;
  tableFn: () => TrialInsightsTableOptions;
  tableOptions: BehaviorSubject<TrialInsightsTableOptions>;
  tableService: TrialInsightsTableModel;
  totalAmount: BehaviorSubject<string>;
  trialInsightsQuery: StoreQuery;

  changeSelectedKey = (buttonKey: string) => {
    const seen = this.sortSeen.includes(buttonKey);

    if (!seen) {
      const defaultSortOrder = this.parseSortOrder(buttonKey);

      this.sortSeen.push(buttonKey);
      this.sortOrder.next(defaultSortOrder);
    }

    this.selectedKey.next(buttonKey);
  };

  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(config: ConstructorConfig<CType>) {
    this.trialInsightsQuery = config.trialInsightsQuery;

    this.store = this.trialInsightsQuery.select(config.slice);
    this.isLoading = this.store.pipe(map((x) => x.isLoading));
    this.isLoadingRemaining = this.store.pipe(map((x) => x.isLoadingRemaining));
    this.data = this.store.pipe(
      map((x) => x.data),
      distinctUntilChanged()
    );

    this.totalAmount = new BehaviorSubject<string>('');
    this.expectedEnrolled = new BehaviorSubject<string>('');
    this.expectedEnrolledExceeded = new BehaviorSubject<boolean>(false);

    this.sortDefaults = config.sortDefaults;

    this.selectedKey = new BehaviorSubject<string>(this.parseSelectedKey());
    this.sortOrder = new BehaviorSubject<SortOrder>(
      this.parseSortOrder(this.selectedKey.getValue())
    );

    // Determines whether a table should use the table's most recent
    // sort order, or the button key's default sort order
    this.sortSeen = [this.selectedKey.getValue()];

    this.tableService = config.tableService;
    this.chartService = config.chartService;

    this.tableFn =
      (this.tableService.createTable && this.tableService.createTable) ||
      (() => ({}) as unknown as TrialInsightsTableOptions);
    this.chartFn =
      (this.chartService.createChart && this.chartService.createChart) ||
      (() => ({}) as unknown as ChartConfiguration<CType>);
    this.legendFn =
      (this.chartService.createLegend && this.chartService.createLegend) ||
      (() => ({}) as unknown as TrialInsightsLegendOptions);

    this.tableOptions = new BehaviorSubject<TrialInsightsTableOptions>(this.tableFn());
    this.chartOptions = new BehaviorSubject<ChartConfiguration<CType>>(this.chartFn());
    this.legendOptions = new BehaviorSubject<TrialInsightsLegendOptions>(this.legendFn());
  }

  parseSelectedKey(): string {
    return this.sortDefaults[0].buttonKey;
  }

  parseSortOrder(selectedKey: string): SortOrder {
    const order = this.sortDefaults.find((sortDefault) => sortDefault.buttonKey === selectedKey);

    return order ? order.defaultOrder : SortOrder.DESC;
  }

  parseStoreData<T>(data: T[]): StoreData<T> {
    const parsedData: StoreData<T> = {
      initialRequest: null,
      remainingRequest: null,
    };

    if (!data) {
      return parsedData;
    }

    parsedData.initialRequest = data[0] ? data[0] : null;
    parsedData.remainingRequest = data[1] ? data[1] : null;

    return parsedData;
  }

  parseStoreDetail<T>(
    parsedData: StoreData<T>,
    selectedKey: string,
    sortOrder: SortOrder
  ): null | StoreDetail<T> {
    const sortDefault = this.sortDefaults.find((currentDefault) => {
      return selectedKey === currentDefault.buttonKey;
    });

    if (!sortDefault) {
      return null;
    }

    if (sortOrder === sortDefault.defaultOrder) {
      return parsedData.initialRequest;
    } else {
      return parsedData.remainingRequest;
    }
  }

  resetState(): void {
    this.totalAmount.next('');
    this.expectedEnrolled.next('');
    this.expectedEnrolledExceeded.next(false);

    this.tableOptions.next(this.tableFn());
    this.chartOptions.next(this.chartFn());
    this.legendOptions.next(this.legendFn());
  }

  toggleSortOrder = () => {
    const current = this.sortOrder.getValue();

    if (current === SortOrder.DESC) {
      this.sortOrder.next(SortOrder.ASC);
    } else {
      this.sortOrder.next(SortOrder.DESC);
    }
  };
}
